import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  SubmissionListResponse,
  SMEQCListParams,
  SMEQCListResponse,
  BatchAssignmentsPayload,
  AssignSMEQC,
  QuickFilterCounts,
} from '../types';
import { WORKSPACE_URLS } from '@data-access/shared';
import { constructHttpParams } from '@shared/utils/api.util';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';

@Injectable()
export class SubmissionsListService {
  private readonly _http = inject(HttpClient);

  /**
   * Load the list of submissions with given parameters.
   * @param submissionListURL - The URL to fetch the submissions from.
   * @param params - The HTTP parameters to include in the request.
   * @returns An Observable containing the list of submissions.
   */
  loadSubmissionList(submissionURL: string, params: HttpParams) {
    return this._http.get<SubmissionListResponse>(submissionURL, {
      params: params,
    });
  }

  /** Fetches per-bucket quick-filter counts under the caller's scoped list URL. */
  loadQuickFilterCounts(submissionURL: string) {
    return this._http.get<QuickFilterCounts>(
      `${submissionURL}quick-filter-counts/`,
      { context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode') },
    );
  }

  /**
   * Load the list of SME or QC users with given parameters.
   * @param params - The parameters containing the attribute type ('sme' or 'qc') and optional additional query parameters.
   * @returns An Observable containing the list of users.
   */
  loadSMEQCList(params: SMEQCListParams) {
    const httpParams = constructHttpParams(params);
    return this._http.get<SMEQCListResponse>(
      `${WORKSPACE_URLS.V2}user-attributes/users/`,
      { params: httpParams },
    );
  }

  public updateSMEQC(
    entityId: string,
    data: { user_id: string; attribute: string },
  ) {
    return this._http.post(
      `${WORKSPACE_URLS.V1}/${entityId}/user-attribute/`,
      data,
    );
  }

  public updateSMEQCNEW(
    entityId: string,
    data: { user_id: string; attribute: string },
  ) {
    return this._http.post(
      `${WORKSPACE_URLS.V2}v2/workspace/${entityId}/user-attribute/`,
      data,
    );
  }

  /**
   * Batch update workspace SME/QC assignments
   * @param attributes - Array of attributes to update with user_id and workspace_id
   * @returns An Observable containing the response
   */
  batchUpdateWorkspaceSMEQC(assignments: BatchAssignmentsPayload) {
    return this._http.put<{ message: string }>(
      `${WORKSPACE_URLS.V2}batch-user-attributes/`,
      assignments,
    );
  }

  /**
   * Batch remove workspace SME/QC assignments
   * @param attributes - Array of attributes to remove with user_id and workspace_id
   * @returns An Observable containing the response
   */
  batchRemoveSMEQC(assignments: BatchAssignmentsPayload) {
    return this._http.delete<{ message: string }>(
      `${WORKSPACE_URLS.V2}batch-user-attributes/`,
      {
        body: assignments,
      },
    );
  }

  /**
   * Update SME/QC for a specific workspace
   * @param workspaceId - The ID of the workspace
   * @param assignment - The assignment payload containing user_id and attribute
   * @returns An Observable containing the response
   */

  updateWorkspaceSMEQC(workspaceId: string, assignment: AssignSMEQC) {
    return this._http.post<{ message: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/user-attribute/ `,
      assignment,
    );
  }

  /**
   * Remove SME/QC for a specific workspace
   * @param workspaceId - The ID of the workspace
   * @param assignment - The assignment payload containing user_id and attribute
   * @returns An Observable containing the response
   */
  removeWorkspaceSMEQC(workspaceId: string, assignment: AssignSMEQC) {
    return this._http.delete<{ message: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/user-attribute/${assignment.user_id}/${assignment.attribute}/`,
    );
  }
}
